<script setup lang="ts">
import "./MStatChip.scss";
import { computed, useAttrs } from "vue";
import type { MStatChipProps } from "./MStatChip.props";
import { mStatChipDefaults } from "./MStatChip.props";

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<MStatChipProps>(), mStatChipDefaults);
const attrs = useAttrs();

const interactive = computed(() => !!attrs.onClick);

const tag = computed(() => (interactive.value ? "button" : "span"));

const classes = computed(() => [
  "m-stat-chip",
  interactive.value && "m-stat-chip--interactive",
  props.selected && "m-stat-chip--selected",
]);

const displayValue = computed(() => {
  if (props.value == null) return undefined;
  if (typeof props.value === "number" && props.value > 99) return "99+";
  return props.value;
});

const DOT_PALETTE: Record<string, string> = {
  blue: "var(--status-blue)",
  red: "var(--status-red)",
  green: "var(--status-green)",
  yellow: "var(--status-yellow)",
};

const resolvedDotColour = computed(() =>
  props.dotColour ? DOT_PALETTE[props.dotColour] ?? props.dotColour : undefined,
);
</script>

<template>
  <component
    :is="tag"
    :class="classes"
    :type="interactive ? 'button' : undefined"
    :aria-pressed="interactive ? selected : undefined"
    v-bind="attrs"
  >
    <span
      v-if="dotColour"
      class="m-stat-chip__dot"
      :style="{ backgroundColor: resolvedDotColour }"
      aria-hidden="true"
    />
    <span class="m-stat-chip__label">{{ label }}</span>
    <span v-if="value != null" class="m-stat-chip__value">{{ displayValue }}</span>
  </component>
</template>
